import { Box, Flex, Text } from '@chakra-ui/react'
import { ResponsiveScatterPlotCanvas } from '@nivo/scatterplot'
import { IResponseBase } from 'api/types'
import { Select, SelectOption } from 'components/Select'
import { useTableChartFilters } from 'contexts/TableChartFilters'
import _, { mean } from 'lodash'
import { useMemo, useState } from 'react'
import CardContainer from '../Shared/CardContainer'
import { xAxisOptions } from './util'

const PricePerDoseChart = ({
  data,
  loading,
}: {
  data: IResponseBase[]
  loading: boolean
}) => {
  const [expanded, setExpanded] = useState<boolean>(false)
  const [hasFiltered, setHasFiltered] = useState<boolean>(false)
  const { addToFilters, tableFilters } = useTableChartFilters()

  const [selectedXAxis, setSelectedXAxis] = useState<SelectOption<string>>({
    value: 'yearDelivered',
    label: 'Year',
  })

  const filteredData = useMemo(
    () => data.filter((x) => !!x.pricePerUnitUsd && !!x[selectedXAxis.value]),
    [data, selectedXAxis.value]
  )

  const mappedData = useMemo(
    () =>
      filteredData.map((x) => ({
        x:
          selectedXAxis.value === 'yearDelivered'
            ? parseInt(x.yearDelivered?.toString() ?? '', 10)
            : (x[selectedXAxis.value]?.toString() ?? ''),
        y: parseFloat(x.pricePerUnitUsd?.toString() ?? ''),
      })),
    [filteredData, selectedXAxis.value]
  )

  const meanData = useMemo(() => {
    return Object.entries(_.groupBy(filteredData, selectedXAxis.value)).map(
      ([groupKey, grouped]) => {
        return {
          x:
            selectedXAxis.value === 'yearDelivered'
              ? parseInt(groupKey?.toString() ?? '', 10)
              : (groupKey?.toString() ?? ''),
          y: mean(
            grouped
              .flatMap((item) => parseFloat(item.pricePerUnitUsd as string))
              .filter((x) => !Number.isNaN(x))
          ),
        }
      }
    )
  }, [filteredData, selectedXAxis.value])

  const chartData = useMemo(
    () => [
      {
        id: 'Value',
        data: mappedData,
      },
      { id: 'Mean', data: meanData },
    ],
    [mappedData, meanData]
  )

  return (
    <CardContainer
      className='clinicalTrialsPerMonth'
      expanded={expanded}
      setExpanded={setExpanded}
      loading={loading}
      cardProps={{ minWidth: '430px' }}
    >
      <Box>
        <Flex alignItems={'center'} h='max-content'>
          <Text fontSize='xs'>Index by:</Text>
          <Select
            value={selectedXAxis}
            isMulti={false}
            onChange={(x: any) => setSelectedXAxis(x)}
            options={xAxisOptions}
            backgroundColor='transparent'
            fontSize={'13px'}
            noBorder
          />
        </Flex>
        <Box w='100%' h={expanded ? '480px' : '230px'}>
          {data.length > 0 ? (
            <ResponsiveScatterPlotCanvas
              data={chartData}
              xScale={{
                type:
                  selectedXAxis.value === 'yearDelivered' ? 'linear' : 'point',
                min: 'auto',
                max: 'auto',
              }}
              axisBottom={{
                legend: selectedXAxis.label,
                legendOffset: expanded ? 52 : 20,
                legendPosition: 'middle',
                truncateTickAt: 50,
                tickRotation: -90,
                format: (v) => {
                  if (!expanded) return ''
                  return v.length > 10 ? (
                    <tspan>
                      {v.substring(0, 10) + '...'}
                      <title>{v}</title>
                    </tspan>
                  ) : (
                    v
                  )
                },
              }}
              axisLeft={{
                legend: 'Price per',
                legendOffset: -50,
                legendPosition: 'middle',
              }}
              margin={{
                top: 20,
                right: 20,
                bottom: expanded ? 70 : 30,
                left: 60,
              }}
            />
          ) : (
            <Flex h={'full'} justifyContent={'center'} alignItems={'center'}>
              <Text
                fontSize={'sm'}
                textAlign={'center'}
                fontWeight={'semibold'}
                color='gray.600'
              >
                No time series data found for current selection
              </Text>
            </Flex>
          )}
        </Box>
      </Box>
    </CardContainer>
  )
}

export default PricePerDoseChart
